import { useToast } from "#imports";
import { useQueryClient } from "@tanstack/vue-query";
import { useAnalytics } from "~/composables/useAnalytics";

export const usePayment = () => {
  const config = useRuntimeConfig();
  const toast = useToast();
  const appState = useAppState();
  const queryClient = useQueryClient();
  const { trackPurchaseHandoffStarted, trackPurchaseHandoffFailed, trackPurchaseHandoffAbandoned } = useAnalytics();

  const processPayment = async (options: {
    artworkId?: string;
    setId?: number;
    artworkIds?: string[];
    isCartCheckout?: boolean;
    cartItems?: any[];
    customerDetails: {
      name: string;
      email: string;
      phone: string;
      pan?: string;
      hasGst?: boolean;
      gstName?: string;
      gstNumber?: string;
      framing?: string;
      framingCharges?: number;
      framingSpecs?: Record<string, any>;
      delivery_instructions?: string;
    };
    shippingAddress: { street: string; city: string; state: string; zip: string; country: string };
  }) => {
    try {
      const { orderId, amount, currency } = await $fetch("/api/orders/create", {
        method: "POST",
        headers: useRequestHeaders(["cookie"]) as any,
        body: {
          artworkId: options.artworkId,
          setId: options.setId,
          artworkIds: options.artworkIds,
          isCartCheckout: options.isCartCheckout,
          cartItems: options.cartItems,
          customerDetails: options.customerDetails,
          shippingAddress: options.shippingAddress,
          delivery_instructions: options.customerDetails.delivery_instructions || null,
        },
      });

      // --- DYNAMIC SCRIPT LOADING ---
      if (!(window as any).Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      if (!config.public.razorpayKeyId) {
        throw new Error("Razorpay Key ID is missing in frontend config");
      }

      let paymentProcessed = false;

      // 2. Configure Razorpay Options
      const razorpayOptions = {
        key: config.public.razorpayKeyId,
        amount: amount,
        currency: currency,
        name: "Quiet Art Movement",
        description: options.isCartCheckout ? "Cart Purchase" : (options.setId ? "Artwork Set Purchase" : "Artwork Purchase"),
        order_id: orderId,
        handler: async function (response: any) {
          paymentProcessed = true;

          // Immediately show the modal in processing state
          appState.isProcessingPayment.value = true;
          appState.isPaymentSuccessModalOpen.value = true;

          // 3. Verify Payment on our Backend
          try {
            const verification = await $fetch("/api/orders/verify", {
              method: "POST",
              body: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                razorpay_response: response, // Send the full response for logging
              },
            });

            if (verification.success) {
              appState.isProcessingPayment.value = false;
              appState.completedOrderData.value = {
                orderId: orderId,
                rpOrderId: response.razorpay_order_id,
                rpPaymentId: response.razorpay_payment_id,
                artworkId: options.artworkId,
                setId: options.setId,
                isCartCheckout: options.isCartCheckout,
                cartItems: options.cartItems,
                customerName: options.customerDetails.name,
                customerEmail: options.customerDetails.email,
                amount: typeof amount === "number" ? amount / 100 : amount,
              };

              // The purchased items are Sold now: refetch the artwork/set page data (useAsyncData)
              // and any listing/feed queries so the page shows it without a manual reload.
              refreshNuxtData();
              queryClient.invalidateQueries();
            }
          } catch (err) {
            console.error("Verification failed", err);
            appState.isProcessingPayment.value = false;
            appState.isPaymentSuccessModalOpen.value = false;
            toast.add({
              title: "Verification Failed",
              description: "Please contact support for payment confirmation.",
              color: "error"
            });
          }
        },
        prefill: {
          name: options.customerDetails.name,
          email: options.customerDetails.email,
          contact: options.customerDetails.phone,
        },
        method: {
          netbanking: true,
          card: true,
          wallet: true,
          upi: true,
        },
        theme: {
          color: "#0a0a0a",
        },
        modal: {
          ondismiss: async function () {
            if (paymentProcessed) return;
            paymentProcessed = true;

            try {
              trackPurchaseHandoffAbandoned({
                artwork_id: options.artworkId,
                set_id: options.setId ? String(options.setId) : undefined,
                handoff_type: "razorpay_modal",
                reason: "user_closed_modal",
              });
            } catch (_) {}

            toast.add({
              title: "Payment Cancelled",
              description: "The payment window was closed.",
              color: "neutral"
            });

            try {
              await $fetch("/api/orders/cancel", {
                method: "POST",
                body: {
                  razorpay_order_id: orderId,
                  razorpay_response: { reason: "User closed the payment modal" }
                },
              });
            } catch (err) {
              console.error("Failed to mark order as cancelled", err);
            }
          },
        },
      };

      // 4. Open Razorpay Modal
      try {
        trackPurchaseHandoffStarted({
          artwork_id: options.artworkId,
          set_id: options.setId ? String(options.setId) : undefined,
          currency: currency || "INR",
          amount: typeof amount === "number" ? amount / 100 : undefined,
          handoff_type: "razorpay_modal",
        });
      } catch (_) {}

      const rzp = new (window as any).Razorpay(razorpayOptions);
      rzp.on("payment.failed", async function (response: any) {
        if (paymentProcessed) return;
        paymentProcessed = true;

        try {
          trackPurchaseHandoffFailed({
            artwork_id: options.artworkId,
            set_id: options.setId ? String(options.setId) : undefined,
            error_message: response.error?.description || "Payment failed",
            error_code: response.error?.code || undefined,
            handoff_type: "razorpay_modal",
          });
        } catch (_) {}

        toast.add({
          title: "Payment Failed",
          description: response.error?.description || "Payment failed",
          color: "error"
        });

        try {
          await $fetch("/api/orders/cancel", {
            method: "POST",
            body: {
              razorpay_order_id: orderId,
              status: "failed",
              razorpay_response: response // Send full failure response
            },
          });
        } catch (err) {
          console.error("Failed to mark order as failed", err);
        }
      });
      rzp.open();
    } catch (error: any) {
      console.error("Payment initialization failed", error);
      toast.add({
        title: "Initialization Error",
        description: error.statusMessage || error.message || "Could not initialize payment",
        color: "error"
      });
    }
  };

  return {
    processPayment,
  };
};
